<template>
  <LayoutApplicationStyle>
    <div v-if="copy" class="pp-wrap">
      <PostPurchaseSteps :current="2" />

      <section class="pp-hero">
        <div class="pp-kick">{{ copy.kick }}</div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <h1 v-html="copy.title"></h1>
        <p class="pp-sub">{{ copy.sub }}</p>
        <button type="button" class="pp-btn pp-teal pp-m-only" @click="toPlans">
          {{ text.takeMe(copy.name) }} <ArrowIcon down />
        </button>
        <div class="pp-incl-grid">
          <div v-for="(item, i) in copy.includes" :key="i" class="pp-inc">
            <CheckIcon :size="16" color="#35A7A0" />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="item"></div>
          </div>
        </div>
        <div class="pp-hero-foot">
          <button
            type="button"
            class="pp-btn pp-teal pp-d-only"
            @click="toPlans"
          >
            {{ text.takeMe(copy.name) }} <ArrowIcon down />
          </button>
          <span v-if="band" class="pp-band">{{ text.pricedFor(band) }}</span>
        </div>
      </section>

      <div id="choose" class="pp-choose">
        <h2>{{ copy.chooseTitle }}</h2>
        <p>{{ copy.chooseSub }}</p>
      </div>

      <div v-if="isComplete" class="pp-plans">
        <button
          v-for="card in cards"
          :key="card.productKey"
          type="button"
          class="pp-plan"
          :class="{ 'pp-sel': selected === card.plan.value }"
          @click="select(card)"
        >
          <span class="pp-chk"><CheckIcon /></span>
          <span v-if="card.tag" class="pp-plan-tag">{{ card.tag }}</span>
          <span class="pp-plan-name">{{ card.name }}</span>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span
            v-for="(row, i) in card.rows"
            :key="i"
            class="pp-row"
            v-html="row"
          ></span>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-if="card.why" class="pp-why" v-html="card.why"></span>
          <span class="pp-price">
            <span class="pp-amt">{{ dollars(card.plan.monthlyPrice) }}</span>
            <span class="pp-per">{{ text.perMonth(trialDuration) }}</span>
          </span>
        </button>
      </div>
      <!-- No band for the declared size, a plan missing at the band, or the list failed. -->
      <div v-else-if="!isLoading" class="pp-contact">
        <p>{{ text.fallback }}</p>
        <a href="tel:18443919499">1-844-391-9499</a>
        <a href="mailto:sales@bambee.com">sales@bambee.com</a>
      </div>

      <div v-if="!copy.includesBelow" class="pp-remind">
        <h3>EVERY {{ copy.name.toUpperCase() }} PLAN INCLUDES</h3>
        <div class="pp-incl-grid">
          <div v-for="(item, i) in copy.includesAll" :key="i" class="pp-inc">
            <CheckIcon :size="16" color="#35A7A0" />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="item"></div>
          </div>
        </div>
      </div>

      <div v-if="isComplete" class="pp-confirm">
        <button
          type="button"
          class="pp-btn"
          :disabled="!selectedCard || saving"
          @click="confirm"
        >
          {{
            selectedCard ? text.continueWith(selectedCard.name) : text.choose
          }}
          <ArrowIcon />
        </button>
        <p v-if="trialOver" class="pp-note">{{ text.afterTrial }}</p>
        <p v-if="error" class="pp-error" role="alert">{{ error }}</p>
      </div>

      <div class="pp-chg">
        <h4>{{ text.changeOfHeart }}</h4>
        <button
          v-for="alt in copy.alternatives"
          :key="alt.level"
          type="button"
          class="pp-alt"
          @click="changeLevel(alt.level)"
        >
          {{ alt.label }}
          <span>{{ alt.cta }} <ArrowIcon /></span>
        </button>
      </div>

      <div v-if="copy.includesBelow" class="pp-remind">
        <h3>EVERY {{ copy.name.toUpperCase() }} PLAN INCLUDES</h3>
        <div class="pp-incl-grid">
          <div v-for="(item, i) in copy.includesAll" :key="i" class="pp-inc">
            <CheckIcon :size="16" color="#35A7A0" />
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div v-html="item"></div>
          </div>
        </div>
      </div>
    </div>
  </LayoutApplicationStyle>
</template>

<script>
import { computed, onMounted, ref, watch } from '@nuxtjs/composition-api';
import LayoutApplicationStyle from '../LayoutApplicationStyle.vue';
import PostPurchaseSteps from '../PostPurchaseSteps.vue';
import ArrowIcon from '../ArrowIcon.vue';
import CheckIcon from '../CheckIcon.vue';
import { Handoff, PlanLevels } from '../../constants/PostPurchase';
import useRegistration, {
  RegistrationStateNames as R,
} from '../../hooks/useRegistration';
import useSupervisionPlans from '../../hooks/useSupervisionPlans';
import { changePlan, subscriptionState } from '@/services/billing.graphql';
import useTrials from '@/hooks/useTrials';
import track from '../../utils/postPurchaseTrack';

const dollars = (n) => `$${Number(n).toLocaleString('en-US')}`;

/**
 * The plan screen for the level the hand-off picked: its hero, its two plans priced at
 * the company's band, "every plan includes", and the change-of-heart links to the other
 * levels. "Continue with {plan}" moves the Recurly subscription onto that plan now and
 * only then advances; an error keeps the customer here with the message.
 */
export default {
  name: 'PlansPage',
  components: {
    ArrowIcon,
    CheckIcon,
    LayoutApplicationStyle,
    PostPurchaseSteps,
  },
  setup(_, { emit }) {
    const { state, updateRegistration } = useRegistration();
    const { level, plans, band, isLoading, isComplete } = useSupervisionPlans();
    const { trialDuration } = useTrials();
    const copy = computed(() => PlanLevels[level.value]);
    const selected = ref(state[R.SUPERVISION_PLAN]?.value || '');
    const saving = ref(false);
    const error = ref('');
    // A paid lead back after the trial ended (a resume link). An immediate change would then
    // charge a prorated amount the screen never shows, so it is scheduled for the next bill
    // instead (BIO-2158 review). Unknown state keeps the in-trial path, the normal case here.
    const trialOver = ref(false);

    const cards = computed(() =>
      copy.value.cards
        .map((card) => ({
          ...card,
          plan: plans.value.find((p) => p.productKey === card.productKey),
        }))
        .filter((card) => card.plan)
    );
    const selectedCard = computed(() =>
      cards.value.find((card) => card.plan.value === selected.value)
    );

    // The cheaper plan is pre-selected (the prototype's DEFAULT_PLAN) unless a plan
    // confirmed earlier at this level is still on offer.
    watch(
      cards,
      (list) => {
        if (list.length && !selectedCard.value) {
          selected.value = list[0].plan.value;
        }
      },
      { immediate: true }
    );

    const planProps = (card) => ({
      productKey: card.plan.productKey,
      planValue: card.plan.value,
      monthlyAmount: card.plan.monthlyPrice,
      band: card.plan.employeeSizeMapping,
    });

    const select = (card) => {
      selected.value = card.plan.value;
      track(state, 'plan-selected', planProps(card));
    };

    const confirm = async () => {
      const card = selectedCard.value;
      const { value, productKey, monthlyPrice, employeeSizeMapping, setupFee } =
        card.plan;
      saving.value = true;
      error.value = '';
      try {
        // Always applied, even when it matches the provisional plan the $1 convert
        // landed on: the server treats it as a no-op and the pick is then on record.
        const timeframe = trialOver.value ? 'RENEWAL' : 'NOW';
        const subscription = await changePlan(
          state[R.COMPANY_ID],
          value,
          timeframe
        );
        track(state, 'plan-confirmed', {
          ...planProps(card),
          timeframe,
          subscriptionState: subscription?.state,
        });
        emit('submit', {
          [R.SUPERVISION_PLAN]: {
            value,
            productKey,
            monthlyPrice,
            employeeSizeMapping,
            setupFee,
            name: card.name,
          },
          [R.PURCHASED_PLAN_ID]: subscription?.planCode || value,
        });
      } catch (e) {
        // The customer stays here with the gateway's message and can retry: the
        // change is idempotent server-side, so a retry after a timeout is safe.
        error.value =
          e.response?.data?.message || e.message || 'Something went wrong.';
        track(state, 'plan-change-failed', {
          ...planProps(card),
          code: e.code,
        });
        window?.DD_RUM?.addError(e);
      } finally {
        saving.value = false;
      }
    };

    const changeLevel = (to) => {
      track(state, 'plan-level-changed', { from: level.value, to });
      updateRegistration({
        [R.SUPERVISION_LEVEL]: to,
        [R.SUPERVISION_PLAN]: null,
      });
      selected.value = '';
      error.value = '';
      window.scrollTo({ top: 0, behavior: 'smooth' });
      track(state, 'plans-viewed', { level: to });
    };

    const toPlans = () =>
      document
        .getElementById('choose')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    onMounted(() => {
      track(state, 'plans-viewed', { level: level.value });
      subscriptionState(state[R.COMPANY_ID])
        .then((s) => {
          trialOver.value = Boolean(s) && s !== 'TRIALING';
        })
        .catch(() => {});
    });

    return {
      text: Handoff,
      trialDuration,
      trialOver,
      copy,
      cards,
      band,
      isLoading,
      isComplete,
      selected,
      selectedCard,
      saving,
      error,
      dollars,
      select,
      confirm,
      changeLevel,
      toPlans,
    };
  },
};
</script>
